import moment from 'moment'
import type {
  RawAppointment,
  RawPlan,
  RawProcedure,
  TreatmentCard,
  TreatmentContact,
  TreatmentProcedure,
  TreatmentStatus,
} from 'app/types/treatment'

export type {
  OutsideVisit,
  RawAppointment,
  RawPlan,
  RawProcedure,
  TreatmentCard,
  TreatmentContact,
  TreatmentPlanRow,
  TreatmentProcedure,
  TreatmentStatus,
} from 'app/types/treatment'

/** Broken appointments older than this no longer count as a live broken case. */
export const BROKEN_MEMORY_MONTHS = 18

/** Statuses that mean the chair was given back, whichever system said it. */
const DEAD_WORDS = /broken|cancel|missed|no.?show|fail/i

export function isBroken(appointment: Pick<RawAppointment, `status` | `pmsStatus`>): boolean {
  return DEAD_WORDS.test(`${appointment.status ?? ``} ${appointment.pmsStatus ?? ``}`)
}

/** A visit taken off the schedule never happened: it is neither a booking nor a break. */
export function isDeleted(appointment: Pick<RawAppointment, `status` | `pmsStatus`>): boolean {
  return /delete/i.test(`${appointment.status ?? ``} ${appointment.pmsStatus ?? ``}`)
}

/** A plan the practice has let go of: `state` CLOSED, a non-open `status`, or a name like "Inactive". */
export function isDeadPlan(plan: RawPlan | undefined): boolean {
  if (!plan) return false
  if ((plan.state ?? ``).toUpperCase() === `CLOSED`) return true
  const status = (plan.status ?? ``).toUpperCase()
  if (status && status !== `OPEN` && status !== `ACTIVE`) return true
  return /inactive|reject|declin|refus/i.test(plan.displayName ?? ``)
}

/**
 * A procedure routinely sits on several plans at once (reworked revisions stay
 * pointed at the same live work), so the live plan must win; taking the first id
 * would let array order decide whether the work reads as live or written off.
 */
export function planForProcedure(
  planIds: string[] | null | undefined,
  plansById: Map<string, RawPlan>,
): RawPlan | undefined {
  const plans = (planIds ?? []).map((id) => plansById.get(id)).filter((plan): plan is RawPlan => !!plan)
  return plans.find((plan) => !isDeadPlan(plan)) ?? plans[0]
}

/**
 * Off-plan procedures are routine hygiene, not treatment the practice presented.
 * Without this filter a cancelled cleaning reads as `broken` treatment.
 */
export function isPlanWork(procedure: { planIds?: string[] | null }): boolean {
  return (procedure.planIds ?? []).length > 0
}

const day = (value?: string | null) => (value ?? ``).slice(0, 10)

/**
 * Whether the patient has been back, or is coming back, since this appointment
 * broke. Any visit counts, not only one carrying the same work: a cleaning
 * after a broken crown visit still means the chair was not abandoned.
 *
 * A past-dated appointment still marked scheduled does not count. The PMS
 * never closed it, and nobody says the patient was there.
 */
function cameBackAfter(broken: RawAppointment, appointments: Map<string, RawAppointment>, now: moment.Moment): boolean {
  const since = day(broken.date)
  for (const appointment of appointments.values()) {
    if (!appointment.date || day(appointment.date) <= since || isBroken(appointment)) continue
    if (/complete/i.test(`${appointment.status ?? ``} ${appointment.pmsStatus ?? ``}`)) return true
    if (moment.utc(appointment.date).isSameOrAfter(now, `day`)) return true
  }
  return false
}

/**
 * A procedure's own state only says PENDING or COMPLETED; whether pending work
 * is booked lives on its appointments, and a broken appointment reads as unbooked.
 *
 * `appointments` is every appointment the patient has, not only this
 * procedure's: a break is only a break while nothing came after it.
 */
export function statusOf(
  procedure: RawProcedure,
  appointments: Map<string, RawAppointment>,
  now: moment.Moment,
): { status: TreatmentStatus; on?: string | null } {
  if ((procedure.state ?? ``).toUpperCase() === `COMPLETED`) return { status: `completed` }

  const mine = (procedure.appointmentIds ?? [])
    .map((id) => appointments.get(id))
    .filter((appointment): appointment is RawAppointment => !!appointment?.date)

  const standing = mine
    .filter((appointment) => !isBroken(appointment) && moment.utc(appointment.date as string).isSameOrAfter(now, `day`))
    .sort((a, b) => day(a.date).localeCompare(day(b.date)))
  if (standing[0]) return { status: `scheduled`, on: day(standing[0].date) }

  const broken = mine
    .filter((appointment) => isBroken(appointment))
    .sort((a, b) => day(a.date).localeCompare(day(b.date)))
  const latest = broken[broken.length - 1]
  if (
    latest &&
    moment.utc(latest.date as string).isAfter(now.clone().subtract(BROKEN_MEMORY_MONTHS, `months`)) &&
    !cameBackAfter(latest, appointments, now)
  ) {
    return { status: `broken`, on: day(latest.date) }
  }

  return { status: `open` }
}

function money(items: TreatmentProcedure[]): number {
  return Math.round(items.reduce((total, item) => total + item.fee, 0))
}

/**
 * One patient's card. The headline is summed from the same procedures the card
 * classifies, not read off `contact.unbookedTreatmentValue`: that rollup diverges
 * from the on-screen breakdown, and keeps its job only as the band query's sort key.
 */
export function buildCard(
  contact: TreatmentContact,
  rawProcedures: RawProcedure[],
  rawPlans: RawPlan[],
  allAppointments: RawAppointment[],
  now: moment.Moment = moment(),
): TreatmentCard {
  const rawAppointments = allAppointments.filter((appointment) => !isDeleted(appointment))
  const plansById = new Map(rawPlans.map((plan) => [plan.id, plan]))
  const appointmentsById = new Map(rawAppointments.map((appointment) => [appointment.id, appointment]))

  const procedures: TreatmentProcedure[] = rawProcedures
    .filter(isPlanWork)
    // A zero-fee line is bookkeeping, not an opportunity.
    .filter((raw) => (raw.feeAmount ?? 0) > 0)
    .map((raw) => {
      const plan = planForProcedure(raw.planIds, plansById)
      const { status, on } = statusOf(raw, appointmentsById, now)
      return {
        code: raw.procedureCode ?? ``,
        description: (raw.description ?? ``).trim(),
        tooth: raw.tooth ?? ``,
        fee: Math.round(raw.feeAmount ?? 0),
        status,
        on,
        plan: plan?.displayName ?? null,
        locationId: raw.locationId ?? null,
        providerId: raw.providerId ?? null,
        priority: raw.priority ?? null,
        planned: day(raw.procedureCreateDate),
        dead: isDeadPlan(plan),
      }
    })
    .sort((a, b) => b.fee - a.fee)

  const at = (status: TreatmentStatus, dead: boolean) =>
    procedures.filter((item) => item.status === status && item.dead === dead)
  const live = procedures.filter((item) => !item.dead)

  const brokenValue = money(at(`broken`, false))
  const scheduled = live.filter((item) => item.status === `scheduled` && item.on)
  const nextFromWork = scheduled.map((item) => item.on as string).sort()[0] ?? null

  // Every standing appointment, not only ones carrying this case.
  const standing = rawAppointments
    .filter(
      (appointment) =>
        !!appointment.date &&
        !isBroken(appointment) &&
        !/complete/i.test(`${appointment.status ?? ``} ${appointment.pmsStatus ?? ``}`) &&
        moment.utc(appointment.date).isSameOrAfter(now, `day`),
    )
    .sort((a, b) => day(a.date).localeCompare(day(b.date)))
  const nextVisit = standing[0]

  // Linked by the plan's procedures, whatever their fee: a visit that carries
  // only a zero-fee line of this plan is still this plan's visit. Off-plan
  // procedures do not count, so a cleaning they sit on stays outside.
  const linked = new Set(rawProcedures.filter(isPlanWork).flatMap((raw) => raw.appointmentIds ?? []))
  const outsideVisits = standing
    .filter((appointment) => !linked.has(appointment.id))
    .map((appointment) => ({
      on: day(appointment.date),
      name: appointment.type || appointment.description || null,
      codes: appointment.procedureCodes ?? [],
    }))
  const nextVisitOn =
    nextFromWork && (!nextVisit || nextFromWork <= day(nextVisit.date))
      ? nextFromWork
      : nextVisit
        ? day(nextVisit.date)
        : null

  /**
   * The last appointment that resolved, and whether it resolved by happening.
   *
   * Completed or broken only: a past-dated appointment still marked scheduled
   * is the PMS not having caught up, and dating a visit from one would put a
   * patient in the chair on a day nobody says they were there.
   */
  const resolved = rawAppointments
    .filter((appointment) => !!appointment.date && moment.utc(appointment.date).isBefore(now, `day`))
    .filter(
      (appointment) =>
        isBroken(appointment) || /complete/i.test(`${appointment.status ?? ``} ${appointment.pmsStatus ?? ``}`),
    )
    .sort((a, b) => day(a.date).localeCompare(day(b.date)))
  const lastVisit = resolved[resolved.length - 1]

  const historyCount = rawAppointments.filter(
    (appointment) =>
      /complete/i.test(`${appointment.status ?? ``} ${appointment.pmsStatus ?? ``}`) && !isBroken(appointment),
  ).length

  return {
    id: contact.id,
    name: contact.name ?? `Unknown patient`,
    cellPhone: contact.cellPhone ?? null,
    locationId: contact.locationId ?? null,
    value: money([...at(`open`, false), ...at(`broken`, false)]),
    excludedValue: money([...at(`open`, true), ...at(`broken`, true)]),
    brokenValue,
    scheduledValue: money(at(`scheduled`, false)),
    completedValue: money(at(`completed`, false)),
    presented: day(contact.lastTreatmentPlanDate),
    // The rollup rather than the appointment read: that read stops at
    // `HISTORY_MONTHS`, and a visit older than the window is still a visit.
    seen: !!contact.lastAppointmentDate,
    lastVisitOn: lastVisit ? day(lastVisit.date) : null,
    lastVisitBroken: lastVisit ? isBroken(lastVisit) : false,
    historyValue: Math.round(contact.productionLtv ?? 0),
    historyCount,
    nextVisitOn,
    nextVisitCodes: nextVisitOn ? scheduled.filter((item) => item.on === nextVisitOn).map((item) => item.code) : [],
    outsideVisits,
    column: brokenValue > 0 ? `broken` : `not-booked`,
    brokenOn:
      live
        .filter((item) => item.status === `broken` && item.on)
        .map((item) => item.on as string)
        .sort()
        .pop() ?? null,
    procedures,
    pipelines: contact.pipelines ?? [],
    treatmentStatus: contact.treatmentStatus ?? null,
  }
}

/** Group raw records by the patient id they belong to, for a page of contacts. */
export function byPatientId<T extends { patientId?: string | null }>(rows: T[]): Map<string, T[]> {
  const out = new Map<string, T[]>()
  for (const row of rows) {
    if (!row.patientId) continue
    const list = out.get(row.patientId) ?? []
    list.push(row)
    out.set(row.patientId, list)
  }
  return out
}

/**
 * Every record belonging to a contact. One person can hold several patient ids,
 * and reading only the first would drop the rest of their treatment.
 */
export function forContact<T extends { patientId?: string | null }>(
  contact: TreatmentContact,
  grouped: Map<string, T[]>,
): T[] {
  return (contact.patientIds ?? []).flatMap((id) => grouped.get(id) ?? [])
}
